/**
 * Elite Fade Designs - Public Chat Widget
 *
 * Floating bottom-right launcher that opens a chat panel.
 * Visitor flow:
 *   1. First time → fills name + email/phone + first message → starts a chat
 *   2. Returning  → session persisted in localStorage → resumes thread
 *
 * Polling every 5s while the panel is open.
 *
 * Mounted by main.js on every non-admin page via initChatWidget().
 */

import './ChatWidget.css';
import { t } from '../../../i18n/index.js';
import {
  startChat, getMessages, sendMessage, markRead,
  getSession, clearSession,
} from '../../../services/chat.service.js';

const POLL_INTERVAL_MS = 5000;

const state = {
  root: null,
  open: false,
  loading: false,
  messages: [],
  lastSeenAt: null,
  pollTimer: null,
  sendingId: 0,    // monotonic counter for optimistic ids
};

// ---------------------------------------------------------------------------
// Public mount
// ---------------------------------------------------------------------------
export function initChatWidget() {
  if (document.querySelector('.efd-chat')) return;  // already mounted

  const root = document.createElement('div');
  root.className = 'efd-chat';
  root.innerHTML = launcherHtml() + panelHtml();
  document.body.appendChild(root);
  state.root = root;

  bindLauncher(root);
  bindPanel(root);
}

// ---------------------------------------------------------------------------
// Launcher
// ---------------------------------------------------------------------------
function launcherHtml() {
  return `
    <button type="button" class="efd-chat__launcher" id="efd-chat-launcher" aria-label="${escapeAttr(label('open', 'Open chat'))}">
      ${iconBubble()}
      <span class="efd-chat__badge" id="efd-chat-badge" hidden></span>
    </button>
  `;
}

function bindLauncher(root) {
  root.querySelector('#efd-chat-launcher').addEventListener('click', togglePanel);
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------
function panelHtml() {
  return `
    <section class="efd-chat__panel" id="efd-chat-panel" aria-hidden="true">
      <header class="efd-chat__header">
        <div class="efd-chat__brand">
          <div class="efd-chat__brand-dot"></div>
          <div>
            <div class="efd-chat__brand-title">${escapeHtml(label('title', 'Chat with us'))}</div>
            <div class="efd-chat__brand-sub" id="efd-chat-status">${escapeHtml(label('subOnline', 'We typically reply within minutes'))}</div>
          </div>
        </div>
        <button type="button" class="efd-chat__icon" id="efd-chat-close" aria-label="${escapeAttr(label('close', 'Close chat'))}">${iconClose()}</button>
      </header>
      <div class="efd-chat__body" id="efd-chat-body">
        <!-- start form OR message thread -->
      </div>
    </section>
  `;
}

function bindPanel(root) {
  root.querySelector('#efd-chat-close').addEventListener('click', closePanel);
}

function togglePanel() {
  state.open ? closePanel() : openPanel();
}

function openPanel() {
  state.open = true;
  const panel = state.root.querySelector('#efd-chat-panel');
  panel.setAttribute('aria-hidden', 'false');
  state.root.classList.add('efd-chat--open');

  const session = getSession();
  if (session) {
    renderThread();
    loadMessages(true);
    startPolling();
  } else {
    renderStartForm();
  }
}

function closePanel() {
  state.open = false;
  const panel = state.root.querySelector('#efd-chat-panel');
  panel.setAttribute('aria-hidden', 'true');
  state.root.classList.remove('efd-chat--open');
  stopPolling();
}

// ---------------------------------------------------------------------------
// Start form
// ---------------------------------------------------------------------------
function renderStartForm() {
  const body = state.root.querySelector('#efd-chat-body');
  body.innerHTML = `
    <form class="efd-chat__form" id="efd-chat-form" novalidate>
      <p class="efd-chat__intro">${escapeHtml(label('intro', 'Hi! Leave your details and your question and we will get back to you.'))}</p>
      <label class="efd-chat__label">${escapeHtml(label('field.name', 'Name'))}
        <input class="efd-chat__input" name="name" required minlength="2" maxlength="100" />
      </label>
      <div class="efd-chat__row">
        <label class="efd-chat__label">${escapeHtml(label('field.email', 'Email'))}
          <input class="efd-chat__input" name="email" type="email" />
        </label>
        <label class="efd-chat__label">${escapeHtml(label('field.phone', 'Phone'))}
          <input class="efd-chat__input" name="phone" type="tel" />
        </label>
      </div>
      <label class="efd-chat__label">${escapeHtml(label('field.message', 'Your message'))}
        <textarea class="efd-chat__input efd-chat__textarea" name="message" required maxlength="2000" rows="3"></textarea>
      </label>
      <div class="efd-chat__form-error" id="efd-chat-form-error" hidden></div>
      <button type="submit" class="efd-chat__send">${escapeHtml(label('startBtn', 'Start chat'))}</button>
      <p class="efd-chat__hint">${escapeHtml(label('hint', 'Email or phone required.'))}</p>
    </form>
  `;
  const form = body.querySelector('#efd-chat-form');
  const errEl = body.querySelector('#efd-chat-form-error');

  // autofocus first field with a small delay so iOS keyboard cooperates
  setTimeout(() => form.querySelector('input[name="name"]')?.focus(), 250);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.hidden = true;
    const fd = new FormData(form);
    const payload = {
      name: (fd.get('name') || '').trim(),
      email: (fd.get('email') || '').trim() || undefined,
      phone: (fd.get('phone') || '').trim() || undefined,
      message: (fd.get('message') || '').trim(),
    };
    if (payload.name.length < 2) {
      errEl.textContent = label('error.name', 'Please enter your name.');
      errEl.hidden = false;
      return;
    }
    if (!payload.email && !payload.phone) {
      errEl.textContent = label('error.contact', 'Please provide either an email or a phone number.');
      errEl.hidden = false;
      return;
    }
    if (payload.message.length < 1) {
      errEl.textContent = label('error.message', 'Please type a message.');
      errEl.hidden = false;
      return;
    }

    setFormBusy(form, true);
    try {
      await startChat(payload);
      renderThread();
      await loadMessages(true);
      startPolling();
    } catch (err) {
      setFormBusy(form, false);
      errEl.textContent = err.message || label('error.generic', 'Could not start chat. Please try again.');
      errEl.hidden = false;
    }
  });
}

function setFormBusy(form, busy) {
  form.querySelectorAll('input, textarea, button').forEach((el) => (el.disabled = busy));
}

// ---------------------------------------------------------------------------
// Thread
// ---------------------------------------------------------------------------
function renderThread() {
  const body = state.root.querySelector('#efd-chat-body');
  body.innerHTML = `
    <ul class="efd-chat__list" id="efd-chat-list" aria-live="polite"></ul>
    <form class="efd-chat__compose" id="efd-chat-compose">
      <textarea class="efd-chat__input efd-chat__textarea" id="efd-chat-input"
                placeholder="${escapeAttr(label('compose', 'Type a message…'))}"
                rows="1" maxlength="2000" required></textarea>
      <button type="submit" class="efd-chat__send efd-chat__send--icon" aria-label="${escapeAttr(label('sendBtn', 'Send'))}">
        ${iconSend()}
      </button>
    </form>
    <div class="efd-chat__toolbar">
      <button type="button" class="efd-chat__link" id="efd-chat-reset">${escapeHtml(label('startOver', 'Start a new chat'))}</button>
    </div>
  `;

  const form  = body.querySelector('#efd-chat-compose');
  const input = body.querySelector('#efd-chat-input');

  // auto-grow textarea
  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 120) + 'px';
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      form.requestSubmit();
    }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    input.style.height = 'auto';

    // optimistic UI
    const tempId = `local-${++state.sendingId}`;
    const optimistic = {
      id: tempId, senderType: 'customer', senderName: 'You',
      body: text, createdAt: new Date().toISOString(), pending: true,
    };
    state.messages.push(optimistic);
    appendMessage(optimistic);

    try {
      const sent = await sendMessage(text);
      // replace optimistic with real
      const idx = state.messages.findIndex((m) => m.id === tempId);
      if (idx >= 0) state.messages[idx] = sent;
      const el = body.querySelector(`[data-id="${tempId}"]`);
      if (el) {
        el.setAttribute('data-id', sent.id);
        el.classList.remove('efd-chat__msg--pending');
      }
    } catch (err) {
      const el = body.querySelector(`[data-id="${tempId}"]`);
      if (el) el.classList.add('efd-chat__msg--failed');
    }
  });

  body.querySelector('#efd-chat-reset').addEventListener('click', () => {
    if (!confirm(label('startOverConfirm', 'Forget this conversation and start a new chat?'))) return;
    clearSession();
    state.messages = [];
    stopPolling();
    renderStartForm();
  });
}

async function loadMessages(initial = false) {
  if (state.loading) return;
  state.loading = true;
  try {
    const since = state.lastSeenAt || (initial ? null : null);
    const fresh = await getMessages(since);
    if (Array.isArray(fresh) && fresh.length) {
      if (initial) {
        state.messages = fresh;
        renderAllMessages();
      } else {
        for (const m of fresh) {
          if (!state.messages.find((x) => x.id === m.id)) {
            state.messages.push(m);
            appendMessage(m);
          }
        }
      }
      const last = fresh[fresh.length - 1];
      state.lastSeenAt = last.createdAt;
    } else if (initial) {
      renderAllMessages();  // empty thread still needs an empty render
    }
    markRead();
  } catch (err) {
    // 401 -> session likely expired, force a restart on next open
    if (err.status === 401) {
      clearSession();
      stopPolling();
      renderStartForm();
    }
  } finally {
    state.loading = false;
  }
}

function renderAllMessages() {
  const list = state.root.querySelector('#efd-chat-list');
  if (!list) return;
  list.innerHTML = state.messages.map(messageHtml).join('');
  scrollToBottom();
}

function appendMessage(msg) {
  const list = state.root.querySelector('#efd-chat-list');
  if (!list) return;
  list.insertAdjacentHTML('beforeend', messageHtml(msg));
  scrollToBottom();
}

function messageHtml(m) {
  const me = m.senderType === 'customer';
  const pending = m.pending ? ' efd-chat__msg--pending' : '';
  const cls = `efd-chat__msg efd-chat__msg--${me ? 'me' : 'them'}${pending}`;
  return `
    <li class="${cls}" data-id="${escapeAttr(m.id)}">
      ${me ? '' : `<div class="efd-chat__msg-sender">${escapeHtml(m.senderName || 'Staff')}</div>`}
      <div class="efd-chat__bubble">${escapeHtml(m.body)}</div>
      <div class="efd-chat__time">${escapeHtml(formatTime(m.createdAt))}</div>
    </li>
  `;
}

function scrollToBottom() {
  const list = state.root.querySelector('#efd-chat-list');
  if (list) list.scrollTop = list.scrollHeight;
}

// ---------------------------------------------------------------------------
// Polling
// ---------------------------------------------------------------------------
function startPolling() {
  stopPolling();
  state.pollTimer = setInterval(() => {
    if (!state.open) return stopPolling();
    loadMessages(false);
  }, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (state.pollTimer) {
    clearInterval(state.pollTimer);
    state.pollTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function label(key, fallback) {
  const fullKey = `chat.${key}`;
  const translated = t(fullKey);
  return translated === fullKey ? fallback : translated;
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }

function iconBubble() {
  // Stylized barber scissors — two circular finger loops on the left,
  // crossing blades pointing to the right, pivot dot at the center.
  return [
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
      '<circle cx="6" cy="7"  r="2.4"/>',
      '<circle cx="6" cy="17" r="2.4"/>',
      '<path d="M8.05 5.85 L20 18"/>',
      '<path d="M8.05 18.15 L20 6"/>',
      '<circle cx="13.5" cy="12" r="0.9" fill="currentColor" stroke="none"/>',
    '</svg>',
  ].join('');
}
function iconClose() {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';
}
function iconSend() {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>';
}
